import { Navigate, Route, Routes } from "react-router";
import { lazy, Suspense, type ReactNode } from "react";
import { RequireAuth, RequireRole, useAuth } from "@/auth";
import { AuthLoading } from "@/auth/AuthLoading";
import { AuthScreen } from "@/screens/AuthScreen";
import { ForgotPasswordScreen } from "@/screens/ForgotPasswordScreen";
import { ResetPasswordScreen } from "@/screens/ResetPasswordScreen";
import { AppShell } from "@/layout/AppShell";
import { ScheduleScreen } from "@/screens/ScheduleScreen";
import { MyReservationsScreen } from "@/screens/MyReservationsScreen";
import { MembershipScreen } from "@/screens/MembershipScreen";
import { ClassDetailScreen } from "@/screens/ClassDetailScreen";
import { TeacherScreen } from "@/screens/TeacherScreen";
import { AnalyticsScreen } from "@/screens/AnalyticsScreen";
import { SettingsScreen } from "@/screens/SettingsScreen";
import { PwaUpdatePrompt } from "@/pwa/PwaUpdatePrompt";
import { InstallPrompt } from "@/pwa/InstallPrompt";
import { OfflineBanner } from "@/components/OfflineBanner";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { RunbookLoadError } from "@/screens/runbooks/RunbookLoadError";

/**
 * App shell + routing skeleton.
 *
 *   /login     — public; sign-in form
 *   /register  — public; create-account form
 *   /forgot-password — public; request a reset link
 *   /reset-password  — public; choose a new password behind the emailed link
 *   /calendar           — student-only; the booking schedule
 *   /my-reservations    — student-only; the student's upcoming bookings
 *   /teacher            — teacher-only; lesson management (placeholder)
 *   /teacher/runbooks   — teacher-only; the Runbook YAML editor
 *   /settings           — any signed-in role; account settings (password)
 *
 * Auth state is provided by `<AuthProvider>` (mounted in `main.tsx`).
 * `<RequireAuth>` and `<RequireRole>` guard the protected routes and
 * redirect unauthenticated users to `/login`.
 */

/** Runbooks editor — lazy so Monaco stays out of the main bundle. */
const RunbookScreen = lazy(() =>
  import("@/screens/RunbookScreen").then((module) => ({
    default: module.RunbookScreen,
  })),
);

/** Public-only routes: bounce already-signed-in users to their home. */
function PublicOnly({ children }: { children: ReactNode }) {
  const { status } = useAuth();
  if (status === "loading") return <AuthLoading />;
  if (status === "authed") return <Navigate to="/" replace />;
  return <>{children}</>;
}

/** Sends `/` to the right home for the signed-in user's role. */
function RoleHome() {
  const { status, currentUser } = useAuth();
  if (status === "loading") return <AuthLoading />;
  if (status !== "authed") return <Navigate to="/login" replace />;
  return (
    <Navigate to={currentUser?.role === "teacher" ? "/teacher" : "/calendar"} replace />
  );
}

export function App() {
  return (
    <AppShell>
      <Routes>
        <Route path="/" element={<RoleHome />} />

        <Route
          path="/login"
          element={
            <PublicOnly>
              <AuthScreen initialMode="login" />
            </PublicOnly>
          }
        />
        <Route
          path="/register"
          element={
            <PublicOnly>
              <AuthScreen initialMode="register" />
            </PublicOnly>
          }
        />

        <Route
          path="/forgot-password"
          element={
            <PublicOnly>
              <ForgotPasswordScreen />
            </PublicOnly>
          }
        />
        <Route
          path="/reset-password"
          element={
            <PublicOnly>
              <ResetPasswordScreen />
            </PublicOnly>
          }
        />

        <Route
          path="/calendar"
          element={
            <RequireRole role="student">
              <ScheduleScreen />
            </RequireRole>
          }
        />

        <Route
          path="/my-reservations"
          element={
            <RequireRole role="student">
              <MyReservationsScreen />
            </RequireRole>
          }
        />

        {/* The student's membership + class credits (issue #249). */}
        <Route
          path="/membership"
          element={
            <RequireRole role="student">
              <MembershipScreen />
            </RequireRole>
          }
        />

        <Route
          path="/teacher"
          element={
            <RequireRole role="teacher">
              <TeacherScreen />
            </RequireRole>
          }
        />

        {/* Studio analytics dashboard (issue #292): teacher-only. */}
        <Route
          path="/teacher/analytics"
          element={
            <RequireRole role="teacher">
              <AnalyticsScreen />
            </RequireRole>
          }
        />

        {/* Runbook editor: teacher-only, lazy-loaded (Monaco is heavy).
            The boundary turns a failed chunk load (e.g. offline) into a
            readable warning instead of a blank route (issue #216). */}
        <Route
          path="/teacher/runbooks"
          element={
            <RequireRole role="teacher">
              <ErrorBoundary
                fallback={() => (
                  <RunbookLoadError onRetry={() => window.location.reload()} />
                )}
              >
                <Suspense fallback={<AuthLoading />}>
                  <RunbookScreen />
                </Suspense>
              </ErrorBoundary>
            </RequireRole>
          }
        />

        {/* Account settings: available to any signed-in role. */}
        <Route
          path="/settings"
          element={
            <RequireAuth>
              <SettingsScreen />
            </RequireAuth>
          }
        />

        {/* Lesson detail is reachable from the student calendar. It
            drives the student booking flow, so it is student-only: a
            teacher who deep-links here is bounced to their own home
            (issue #272). */}
        <Route
          path="/lessons/:id"
          element={
            <RequireRole role="student">
              <ClassDetailScreen />
            </RequireRole>
          }
        />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      {/* PWA update/offline toast — fixed overlay, independent of routes. */}
      <PwaUpdatePrompt />
      {/* Install nudge (Chrome/Android + iOS A2HS) — fixed overlay. */}
      <InstallPrompt />
      {/* Connectivity banner — top-fixed, independent of routes. */}
      <OfflineBanner />
    </AppShell>
  );
}
